// ========== Imports ==========
// React core
import { useEffect, useState } from "react";

// UI components
import { MapPin, MapPinOff, Home, Plus } from "lucide-react";

// Custom components
import AddHomeAddressModal from "../../../shared/modal/AddHomeAddressModal";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { updateDeliveryMethod, updatePickupId } from "../../../store/reducer/authSlice";
import { updateDeliveryAddress } from "../../../store/thunk/userThunk";
import { fetchDeliveryMethod } from "../../../store/thunk/addressThunk";
import { fetchCartInfo } from "../../../store/thunk/cartThunk";
import { fetchUserProfile } from "../../../store/thunk/authThunk";

// Utils
import toast from "react-hot-toast";

// ========== Component ==========
const DeliveryMethodFields = ({
  bottomSheet = false,
  onAddressSelect,
  page = "",
  charges = {},
  deliveryMethods = [],
  skipApiCall = false, // Skip API call when parent handles the update
  initialDeliveryMethod = null, // Optional: initial delivery method from parent
  initialPickupId = null, // Optional: initial pickup_id from parent
}) => {
  // ========== Redux hooks ==========
  const dispatch = useDispatch();
  const { availableDeliveryMethods, availableDeliveryMethodsLoading } = useSelector((state) => state.address);
  const { userInfo } = useSelector((state) => state.auth);

  // ========== State management ==========
  const [activeTab, setActiveTab] = useState("home"); // Default to "home"
  const [selectedAddress, setSelectedAddress] = useState();
  const [selectedAddressID, setSelectedAddressID] = useState();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  // Extract lists from availableDeliveryMethods
  const homeList = availableDeliveryMethods?.find(method => method.short_name === "home")?.details || [];
  const pickupList = availableDeliveryMethods?.find(method => method.short_name === "delivery-partner")?.details || [];
  // ========== Effects ==========
  // Load delivery methods on component mount if not already loaded
  useEffect(() => {
    if (availableDeliveryMethods.length === 0) {
      dispatch(fetchDeliveryMethod());
    }
  }, [dispatch, availableDeliveryMethods.length]);

  // Set active tab from initial values (from parent) or user info, default to "home" if nothing selected
  useEffect(() => {
    // Priority: initialDeliveryMethod (from parent) > userInfo.delivery_method > "home" (default)
    if (initialDeliveryMethod) {
      setActiveTab(initialDeliveryMethod);
    } else if (userInfo?.delivery_method) {
      setActiveTab(userInfo.delivery_method);
    } else {
      // Default to "home" if no delivery method is selected
      setActiveTab("home");
    }

    // Priority: initialPickupId (from parent) > userInfo.pickup_id
    if (initialPickupId) {
      setSelectedAddressID(initialPickupId);
    } else if (userInfo?.pickup_id) {
      setSelectedAddressID(userInfo.pickup_id);
    }
  }, [userInfo, initialDeliveryMethod, initialPickupId]);

  // ========== Handlers ==========
  // Handle delivery address selection
  const handleDeliveryAddress = (addressID) => {
    const savedPromo = localStorage.getItem("hf_promoCode");
    setSelectedAddressID(addressID);

    // Find selected address details based on active tab
    let selectedAddressDetails = null;
    if (activeTab === "home") {
      selectedAddressDetails = homeList.find((addr) => addr.pickup_id === addressID);
    } else if (activeTab === "delivery-partner") {
      selectedAddressDetails = pickupList.find((point) => point.pickup_id === addressID);
    }

    // Notify parent component immediately
    if (selectedAddressDetails && onAddressSelect) {
      const addressString = selectedAddressDetails.address || selectedAddressDetails.name || "Selected Address";
      onAddressSelect({
        address: addressString,
        method: activeTab,
        pickup_id: addressID,
        pickupPoint: activeTab === "delivery-partner" ? selectedAddressDetails : null
      });
    }

    // Skip API call if parent handles the update
    if (skipApiCall) {
      return;
    }

    // Update delivery address via API
    dispatch(
      updateDeliveryAddress({
        user_id: userInfo?.user_id,
        pickup_id: addressID,
        delivery_method: activeTab,
        product_price: 0,
        product_id: "",
        discount_code: "",
        action: "update-delivery-address",
        ...(savedPromo ? { promo_code: savedPromo } : {}),
      })
    )
      .then((resultAction) => {
        if (updateDeliveryAddress.fulfilled.match(resultAction)) {
          // Update Redux state with API response
          dispatch(updatePickupId(resultAction?.payload?.pickup_id));
          dispatch(updateDeliveryMethod(resultAction?.payload?.delivery_method));
          setActiveTab(resultAction?.payload?.delivery_method);

          // Update local state with API address
          const apiAddress = resultAction?.payload?.pickup_address;
          if (apiAddress) {
            setSelectedAddress(apiAddress);

            // Notify parent if API address differs from initial selection
            if (onAddressSelect && apiAddress !== selectedAddressDetails?.address) {
              onAddressSelect({
                address: apiAddress,
                method: activeTab,
                pickup_id: addressID,
                pickupPoint: activeTab === "delivery-partner" ? selectedAddressDetails : null
              });
            }
          }

          // Refresh cart to update delivery charges
          dispatch(fetchCartInfo());

          // Refresh user profile to sync latest delivery preferences
          dispatch(fetchUserProfile());
        } else if (updateDeliveryAddress.rejected.match(resultAction)) {
          const error = resultAction.payload || resultAction.error.message || "An unknown error occurred.";
          toast.error(error?.detail || "Failed to update delivery address");
        }
      })
      .catch((error) => {
        toast.error("Failed to update delivery address");
      });
  };

  // Handle tab/method change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // setSelectedAddressID(null);
    // setSelectedAddress(null);
  };

  // ========== Helpers ==========
  // Get active delivery methods from availableDeliveryMethods or fallback to deliveryMethods prop
  const activeDeliveryMethods = availableDeliveryMethods?.length > 0
    ? availableDeliveryMethods.filter((method) => method.status === 1).sort((a, b) => a.display_order - b.display_order)
    : deliveryMethods?.length > 0
    ? deliveryMethods.filter((method) => method.status === 1).sort((a, b) => a.display_order - b.display_order)
    : [{ id: 1, name: "Home", short_name: "home", price: 9.0, status: 1 }]; // Fallback

  // ========== Render ==========
  return (
    <div className="tracking-[o.7px]">
      {/* Delivery method tabs */}
      <div className={`flex flex-wrap letter-spacing-07 gap-4 ${bottomSheet ? "mb-3" : "md:mb-0 mb-3 pb-5"}`}>
        {activeDeliveryMethods.map((method) => (
          <button
            key={method.id}
            onClick={() => handleTabChange(method.short_name)}
            className={`px-5 py-3 text-sm md:text-md md:px-6 md:py-3 rounded-full font-medium transition-all duration-200 shadow-[0_3px_10px_rgb(0,0,0,0.2)]
              ${activeTab === method.short_name
                ? "main-btn md:bg-gradient2 border-opacity-50 text-white"
                : `${bottomSheet ? "text-gray-300" : "text-primary-100"}`
              }`}
          >
            {method.name} (${method.price?.toFixed(2)})
          </button>
        ))}
      </div>

      {/* Address selection based on active tab */}
      {activeDeliveryMethods
        .filter((method) => method.short_name === activeTab)
        .map((method) => {
          // Home delivery addresses
          if (method.short_name === "home") {
            return (
              <div key={method.id}>
                {/* Add new address button */}
                <button
                  onClick={() => setIsAddressModalOpen(true)}
                  className={`letter-spacing-1 flex items-center justify-center gap-2 py-2 text-md font-medium ${
                    bottomSheet ? "text-white" : "text-red-500"
                  }`}
                >
                  <Plus size={16} color={bottomSheet ? "white" : "currentColor"} />
                  Add New Address
                </button>

                {/* Home address list */}
                <div className="space-y-3">
                  <div className={`block overflow-y-auto ${homeList?.length === 0 ? "h-fit" : bottomSheet ? "h-[200px]" : "max-h-60"}`}>
                    <div className="space-y-0">
                      {homeList?.length > 0 ? (
                        homeList.map((address) => (
                          <label key={address.pickup_id} className="flex items-start gap-3 cursor-pointer px-3 py-2 mt-0">
                            <input
                              type="radio"
                              name="address"
                              value={address.pickup_id}
                              checked={selectedAddressID === address.pickup_id}
                              onChange={() => handleDeliveryAddress(address.pickup_id)}
                              className="w-4 mt-2 h-4 text-primary-100 border-primary-100"
                            />
                            <div>
                              <div className="text-md font-medium letter-spacing-1">{address.name}</div>
                              <div className="flex items-center gap-1 letter-spacing-07 text-sm">
                                <MapPin className={`h-4 w-4 ${bottomSheet ? "text-white" : "text-primary-100"}`} />
                                {address.address}
                              </div>
                            </div>
                          </label>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 px-4">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${bottomSheet ? 'bg-white/10' : 'bg-red-50'}`}>
                            <Home className={`w-6 h-6 ${bottomSheet ? 'text-white/50' : 'text-primary-100/60'}`} strokeWidth={1.5} />
                          </div>
                          <p className={`text-sm font-medium mb-1 ${bottomSheet ? 'text-white/80' : 'text-gray-700'}`}>
                            No Saved Addresses
                          </p>
                          <p className={`text-xs text-center max-w-[200px] ${bottomSheet ? 'text-white/40' : 'text-gray-400'}`}>
                            Tap "Add New Address" above to get started
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          // Pickup point addresses (delivery-partner)
          if (method.short_name === "delivery-partner") {
            return (
              <div key={method.id} className="space-y-3">
                <div className={`block overflow-y-auto ${pickupList?.length === 0 ? "h-fit" : bottomSheet ? "h-[200px]" : "max-h-60"}`}>
                  <div className="space-y-0">
                    {pickupList?.length > 0 ? (
                      pickupList.map((point) => (
                        <label key={point.pickup_id} className="flex items-start gap-3 cursor-pointer px-3 py-2 mt-0">
                          <input
                            type="radio"
                            name="pickupPoint"
                            value={point.pickup_id}
                            checked={selectedAddressID === point.pickup_id}
                            onChange={() => handleDeliveryAddress(point.pickup_id)}
                            className="w-4 h-4 text-primary-100 border-primary-100 mt-2"
                          />
                          <div>
                            <div className="text-md font-medium letter-spacing-1">{point.name}</div>
                            <div className="flex items-center gap-1 letter-spacing-07 text-sm">
                              <MapPin className={`h-4 w-4 ${bottomSheet ? "text-white" : "text-primary-100"}`} />
                              {point.address}
                            </div>
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 px-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${bottomSheet ? 'bg-white/10' : 'bg-red-50'}`}>
                          <MapPinOff className={`w-6 h-6 ${bottomSheet ? 'text-white/50' : 'text-primary-100/60'}`} strokeWidth={1.5} />
                        </div>
                        <p className={`text-sm font-medium mb-1 ${bottomSheet ? 'text-white/80' : 'text-gray-700'}`}>
                          No Pickup Points Available
                        </p>
                        <p className={`text-xs text-center max-w-[220px] ${bottomSheet ? 'text-white/40' : 'text-gray-400'}`}>
                          Pickup locations are currently unavailable.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}

      {/* Add home address modal */}
      <AddHomeAddressModal open={isAddressModalOpen} setOpen={setIsAddressModalOpen} />
    </div>
  );
};

export default DeliveryMethodFields;
